/**
 * Types + fetchery dla pluginu `c_electronic_signs` rozszerzajacego shell
 * `c_user_accounts` (dokumenty do podpisu zalogowanego usera - lista + podglad
 * oryginalu + wychodzacy link do podpisu).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET /IRESTAPI/v1/C_ELECTRONIC_SIGNS_CONTROLLER/userDocuments (caly zbior + truncated)
 *
 * Podglad oryginalu jest zaszyfrowanym linkiem `preview_href` (`/IGETFILE/{blob}`)
 * generowanym po stronie serwera (`C_ELECTRONIC_SIGNS_CONTROLLER::getUserDocumentPreviewUrl`).
 * Wzorzec spojny z `C_FACTURE_CONTROLLER::getUserInvoicePdfUrl` - id dokumentu
 * jest w tamper-proof payloadzie, ownership sprawdzany po stronie backendu w
 * handlerze `userDocumentPreviewFile` (parytet z FILE_ACCESS_LOGED + wlasny
 * check `authorized_users`). Browser nawiguje bezposrednio (bez proxy z Bearer).
 *
 * Podpis (`sign_href`) jest flow WYCHODZACYM - absolutny URL do formularza podpisu na
 * backendzie Imperial (m_electronic_signs signForm: ePUAP / kwalifikowany / skan).
 *
 * Frontend route handler proxy dla listy (Bearer) w `app/api/iiapi/c-electronic-signs/documents/`.
 */

export type UserSignDocument = {
  id: number;
  /** Nazwa dokumentu. */
  name: string;
  /** Data dodania (Y-m-d H:i:s). */
  add_date: string;
  /** Metoda podpisu (1=skan, 2=ePUAP, 3=kwalifikowany). Etykiety mapuje view. */
  method: number;
  /** Status dokumentu (1=nowy, 2=przekazany, 3=w trakcie, 4=zakonczony). Etykiety mapuje view. */
  id_status: number;
  /** Status podpisu usera (1=oczekuje, 2=w trakcie, 3=podpisano). Etykiety mapuje view. */
  user_sign_status: number;
  /** Czy user moze teraz podpisac dokument. */
  signable: boolean;
  /** Absolutny URL do formularza podpisu na backendzie (null gdy !signable). */
  sign_href: string | null;
  /**
   * Absolutny zaszyfrowany URL podgladu oryginalu (`/IGETFILE/{blob}` z backendu).
   * Generowany przez `C_ELECTRONIC_SIGNS_CONTROLLER::getUserDocumentPreviewUrl` -
   * id dokumentu w tamper-proof payloadzie, ownership sprawdzany server-side.
   * `null` gdy brak pliku oryginalu. Browser nawiguje bezposrednio (bez proxy).
   */
  preview_href: string | null;
};

export type UserSignDocumentsResponse = {
  ok: true;
  data: {
    documents: UserSignDocument[];
    /** `true` gdy lista zostala obcieta do limitu serwera (500). */
    truncated: boolean;
  };
};

/**
 * Pobiera liste dokumentow do podpisu usera z route handler proxy.
 *
 * @returns `{ documents, truncated }`
 * @throws Error gdy fetch fail / response !ok (caller obsluguje przez try/catch).
 */
export async function fetchUserSignDocuments(): Promise<{
  documents: UserSignDocument[];
  truncated: boolean;
}> {
  const res = await fetch("/api/iiapi/c-electronic-signs/documents", { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Fetch documents failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | UserSignDocumentsResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body ? body.message ?? "Fetch failed" : "Invalid response shape",
    );
  }
  return {
    documents: body.data?.documents ?? [],
    truncated: body.data?.truncated ?? false,
  };
}
